import { records } from '@saucelabs/sl-component-performance-preview';
import { utils } from '@saucelabs/sl-component-baseline-chart';
import { constants as performanceConstants } from '@saucelabs/sl-module-performance';

import { MAX_SELECTED_BASELINE, RIGHT_AXIS_METRICS } from './constants';

const {
  METRICS_MAP,
  SCROLL_TIMINGS_MAP,
  SCROLL_SCORING_MAP,
  PERFORMANCE_TYPE,
  FILMSTRIP_METRIC_MAP,
  REPORT_METRICS,
  JANK_METRICS,
} = performanceConstants;

export const JANK_METRICS_MAP = SCROLL_TIMINGS_MAP.merge(SCROLL_SCORING_MAP);

export const prepareMetricsChartData = (
  metrics, filmStripMetricMap, cy,
) => Array.from(filmStripMetricMap.entries())
  .filter((entry) => metrics.getIn([entry[0], 'value']))
  .map(([key, value]) => ({
    title: value.get('title'),
    abbreviatedTitle: value.get('abbreviatedTitle'),
    ts: metrics.getIn([key, 'value']),
    cy,
  }));

export const getMaxValue = (maxSnapshotValue, metrics, metricsMap, adjustment) => {
  const maxValue = Math.max(
    ...Array.from(metricsMap.entries())
      .filter((entry) => metrics.getIn([entry[0], 'value']))
      .map((entry) => metrics.getIn([entry[0], 'value'])),
  );
  /*
   * To handle last snapshot cut off issue,
   * Increase xAxis max value by adjustment of maxSnapshotValue
   */
  return maxValue > maxSnapshotValue ?
    maxValue :
    maxSnapshotValue + ((maxSnapshotValue * adjustment) / 100);
};

export const prepareURLItemFromJobInfo = ({
  page_url: url, order_index: orderIndex, type, loader_id: loaderId,
}) => new records.UrlInfoRecord({
  url,
  orderIndex,
  traceType: type,
  loaderId,
});

export const getHSLColor = (count, idx) => `hsl(${(idx * (360 / count)) % 360}, 45%, 55%)`;

/**
 * getHSL color hue starts at 207 to limit the number of colors rendered
 * @param  {number} count  amount of colors to generate
 * @return {string[]}      list of generated HLS colors
 */
export const getHSLColorsList = (count) => {
  const colors = [];

  for (let i = 0; i < count; i += 1) {
    colors[i] = getHSLColor(count, i);
  }

  return colors;
};

export const metricValueLabel = (value, name) => {
  if (typeof value === 'undefined' || value === null) {
    return '-  -';
  }

  const parsedValue = parseInt(value, 10);
  if (name === 'Page Weight') {
    return `${(parsedValue / 1000).toFixed(2)} KB`;
  }
  if (name === 'Requests') {
    return `${parsedValue.toFixed(0)}`;
  }
  return `${parsedValue} ms`;
};

const sortStrings = (prev, curr) => prev < curr;
const sortNumbers = (prev, curr) => curr - prev;

/**
 * return a list of 6 metrics and push the rest of it to "others"
 */
export const getMetricList = (metrics, metricCount) => Array.from(metrics).map((entryRecord) => ({
  title: entryRecord.get('name'),
  value: entryRecord.get('time'),
})).sort((prev, curr) => {
  const prevField = prev.value;
  const currField = curr.value;
  const areNumbers = Number.isFinite(prevField) && Number.isFinite(currField);
  const sortFn = areNumbers ? sortNumbers : sortStrings;
  return sortFn(prevField, currField);
}).reduce((sortedMetrics, metric, i) => {
  if (i < metricCount - 1) {
    sortedMetrics.push(metric);
    return sortedMetrics;
  }

  if (sortedMetrics[sortedMetrics.length - 1].title === 'Others') {
    sortedMetrics[sortedMetrics.length - 1].value += metric.value; // eslint-disable-line
    return sortedMetrics;
  }

  metric.title = 'Others'; // eslint-disable-line
  sortedMetrics.push(metric);
  return sortedMetrics;
}, []);

// generate colors to associate with each metric
export const generateMapColors = (map = METRICS_MAP) => {
  const colors = getHSLColorsList(map.size);
  return map.withMutations((newMap) => {
    map.keySeq().forEach((key, index) => {
      newMap.setIn([key, 'color'], colors[index]);
    });
  });
};

export const getMetricsGraphMap = (type = PERFORMANCE_TYPE.HARD) => generateMapColors(
  type === PERFORMANCE_TYPE.JANK ? JANK_METRICS_MAP : METRICS_MAP,
);

export const parseMetrics = (metrics) => metrics.map((name) => ({
  name,
  axis: RIGHT_AXIS_METRICS.some(([rightAxisName]) => rightAxisName === name) ? 1 : 0,
}));

export const setAdditionalMetricsValue = (metricsData, index) => (
  Array.isArray(metricsData) && metricsData[index] ?
    utils.parseBaselineValue(metricsData[index].r) :
    undefined);

// Prepare rawData by merging values from selected metrics values in single object
// and pass that rawData to utils.prepareBaselineChartData to prepare baseline chart data
export const prepareHistoryDetailChartData = ({
  isBaselineDataSufficient, metricsHistoryDetail, selectedMetrics, shouldShowBaseline,
}) => {
  if (!isBaselineDataSufficient || !selectedMetrics.length) {
    return [];
  }
  const [baselineMetric, ...otherMetrics] = selectedMetrics;
  if (!metricsHistoryDetail.get(baselineMetric.name)) {
    return [];
  }
  const rawData = metricsHistoryDetail.get(baselineMetric.name).map((baselineData, index) => ({
    ...baselineData,
    ...(otherMetrics.reduce((ac, { name }) => ({
      ...ac,
      [name]: setAdditionalMetricsValue(metricsHistoryDetail.get(name), index),
    }), {})),
  }));
  return utils.prepareBaselineChartData({
    data: rawData,
    shouldIncludeChangePoint: shouldShowBaseline,
  });
};

// if length is less than max_no then return startIndex: 0 and endIndex: length - 1
// otherwise startIndex: length/2 - max_no/2 and endIndex: length/2 + max_no/2
export const getSelectedIndex = (length) => ({
  startIndex: length < MAX_SELECTED_BASELINE ?
    0 : Math.floor((length / 2) - (MAX_SELECTED_BASELINE / 2)),
  endIndex: length < MAX_SELECTED_BASELINE ?
    length - 1 : Math.floor((length / 2) + (MAX_SELECTED_BASELINE / 2)),
});

export const getMetricsMap = (perfType) => (perfType === PERFORMANCE_TYPE.JANK ?
  JANK_METRICS_MAP : METRICS_MAP
);

export const parseSelectedJobMetricsValue = (metricsData, perfType) => getMetricsMap(perfType)
  .map((value, key) => ({
    value: utils.defaultParseValue(metricsData[key], value.get('name')),
    name: value.get('name'),
  })).toJS();

export const getPerfType = (metricsData) => (
  Object.keys(metricsData).includes('fpsScore') ? PERFORMANCE_TYPE.JANK : PERFORMANCE_TYPE.HARD
);

export const shouldChangeMetricSelection = (perfType, selectedMetric) => (
  !Array.from(getMetricsMap(perfType).keys()).includes(selectedMetric[0].name)
);

/**
 * To identify lighthouse version using metrics,
 * Check if new metric (cumulativeLayoutShift) belongs to metrics
 * @param {Object} metrics
 * @returns {boolean} true if metrics contains cumulativeLayoutShift
 */
export const isLighthouseV6 = (metrics) => (
  metrics ? Object.keys(metrics).includes('cumulativeLayoutShift') : false
);

/**
 * Get list of metrics
 * @param {string} perfType
 * @param {boolean} includeLighthouseV6
 * @returns {Array} list of metrics
 */
export const getMetricsList = (perfType, includeLighthouseV6 = false) => {
  if (perfType === PERFORMANCE_TYPE.JANK) {
    return Array.from(JANK_METRICS.entries());
  }

  return Array.from((includeLighthouseV6 ? REPORT_METRICS : FILMSTRIP_METRIC_MAP).entries());
};
